import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Plus, HardDrive, ShieldCheck, ShieldAlert, Wrench } from 'lucide-react'
import { toast } from 'sonner'
import { AddressPicker, type AddressValue } from '@/components/address-picker'
import { useAssets, useAssetHistory, warrantyState, type Asset, type WarrantyState } from '@/hooks/use-assets'
import { useClients, clientName } from '@/hooks/use-clients'
import {
  Page, PageHeader, Card, Table, Trow, SkeletonRows, EmptyState,
  Pagination, TabPills, SearchInput, btnPrimary, btnSecondary,
} from '@/components/rm'
import { Modal, Field, Input, Select } from '@/components/rm/form'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/equipment')({ component: AssetsPage })

const COLS = '1.5fr 1.1fr 1fr 130px 120px'

const fmtDate = (iso: string | null) =>
  iso ? phFormat(new Date(iso), 'date', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

const WARRANTY_LABEL: Record<WarrantyState, string> = {
  active: 'In warranty', expiring: 'Expiring soon', expired: 'Out of warranty', none: 'No warranty date',
}

function AssetsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [warranty, setWarranty] = useState<WarrantyState | ''>('')
  const [page, setPage] = useState(0)
  const [open, setOpen] = useState<Asset | null>(null)
  const [adding, setAdding] = useState(false)

  const { assets, total, pageSize, isLoading, isError, error, refetch, create, archive } =
    useAssets({ search, warranty, page })
  const { clients } = useClients()

  const [form, setForm] = useState({
    name: '', contact_id: '', address: '', category: '', make: '', model: '',
    serial_number: '', location_note: '', installed_at: '', warranty_expires_at: '',
  })

  const nameFor = (cid: string | null) => {
    if (!cid) return '—'
    const c = clients.find((x) => x.id === cid)
    return c ? clientName(c) : '—'
  }

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Give the equipment a name.')
    try {
      await create.mutateAsync({
        name: form.name.trim(),
        contact_id: form.contact_id || null,
        address: form.address || null,
        category: form.category || null,
        make: form.make || null,
        model: form.model || null,
        serial_number: form.serial_number || null,
        location_note: form.location_note || null,
        installed_at: form.installed_at || null,
        warranty_expires_at: form.warranty_expires_at || null,
      })
      setAdding(false)
      setForm({ name: '', contact_id: '', address: '', category: '', make: '', model: '', serial_number: '', location_note: '', installed_at: '', warranty_expires_at: '' })
      toast.success('Equipment added')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add that equipment')
    }
  }

  return (
    <Page>
      <PageHeader
        title="Equipment"
        subtitle={`${total} unit${total === 1 ? '' : 's'}`}
        actions={<button className={btnPrimary} onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> New equipment</button>}
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <TabPills
          chips={[
            { id: '', label: 'All', count: total },
            { id: 'expiring', label: 'Expiring soon' },
            { id: 'expired', label: 'Out of warranty' },
            { id: 'active', label: 'In warranty' },
          ]}
          active={warranty}
          onChange={(id) => { setWarranty(id as WarrantyState | ''); setPage(0) }}
        />
        <div className="w-full sm:w-72">
          <SearchInput
            value={search}
            onChange={(v) => { setSearch(v); setPage(0) }}
            placeholder="Search serial, make, model or address"
          />
        </div>
      </div>

      <div className="mt-5">
        <Card>
          {isLoading ? (
            <SkeletonRows count={5} />
          ) : isError ? (
            // Distinct from "no assets" — an empty register shown because the fetch failed would
            // have a trade person standing at a site believing there is nothing on file.
            <div className="px-5 py-10 text-center">
              <p className="text-[14px] font-semibold text-token-primary">Couldn’t load your equipment</p>
              <p className="mt-1 text-[13px] text-token-secondary">{error?.message ?? 'Please try again.'}</p>
              <button className={btnSecondary + ' mt-4'} onClick={() => refetch()}>Try again</button>
            </div>
          ) : assets.length === 0 ? (
            <EmptyState
              label={search || warranty
                ? 'No equipment matches that filter.'
                : 'No equipment yet. Record the units you service — serial, warranty and every job they’ve had.'}
              cta={search || warranty ? undefined : 'New equipment'}
              onCta={search || warranty ? undefined : () => setAdding(true)}
            />
          ) : (
            <Table
              cols={COLS}
              header={<><span>Equipment</span><span>Client</span><span>Location</span><span>Installed</span><span className="text-right">Warranty</span></>}
            >
              {assets.map((a, idx) => {
                const w = warrantyState(a)
                return (
                  <Trow key={a.id} cols={COLS} last={idx === assets.length - 1} onClick={() => setOpen(a)}>
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 truncate font-bold text-token-primary">
                        <HardDrive className="h-3.5 w-3.5 flex-none text-token-secondary" />
                        {a.name}
                      </span>
                      <span className="block truncate text-[12px] font-normal text-token-secondary">
                        {[a.make, a.model, a.serial_number].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </span>
                    <span className="truncate text-[13px] text-token-secondary">{nameFor(a.contact_id)}</span>
                    <span className="truncate text-[13px] text-token-secondary">{a.address || '—'}</span>
                    <span className="text-[13px] tabular-nums text-token-secondary">{fmtDate(a.installed_at)}</span>
                    <span className="text-right">
                      <span className={
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-bold ' +
                        (w === 'expired' ? 'bg-token-danger-surface text-token-danger'
                          : w === 'expiring' ? 'bg-token-warning-surface text-token-warning'
                          : w === 'active' ? 'bg-token-success-surface text-token-success'
                          : 'bg-canvas-subtle text-token-secondary')
                      }>
                        {w === 'expired' ? <ShieldAlert className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
                        {w === 'none' ? '—' : fmtDate(a.warranty_expires_at)}
                      </span>
                    </span>
                  </Trow>
                )
              })}
              <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
            </Table>
          )}
        </Card>
      </div>

      {open && (
        <AssetDetailModal
          asset={open}
          clientName={nameFor(open.contact_id)}
          onClose={() => setOpen(null)}
          onArchive={async () => {
            try { await archive.mutateAsync(open.id); setOpen(null); toast.success('Equipment archived') }
            catch (e) { toast.error(e instanceof Error ? e.message : 'Could not archive') }
          }}
          onOpenJob={(id) => { setOpen(null); navigate({ to: '/jobs/$id', params: { id: String(id) } }) }}
        />
      )}

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="New equipment"
        wide
        footer={<>
          <button className={btnSecondary} onClick={() => setAdding(false)}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={create.isPending}>{create.isPending ? 'Adding…' : 'Add equipment'}</button>
        </>}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Living room split system" /></Field>
          <Field label="Client">
            <Select value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })}>
              <option value="">No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
            </Select>
          </Field>
          <AddressPicker
            parts={false}
            value={{ address: form.address, line1: null, line2: null, city: null, state: null, postal_code: null, manual_address: false }}
            onChange={(a: AddressValue) => setForm({ ...form, address: a.address ?? '' })}
          />
          <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="HVAC, hot water, backflow…" /></Field>
          <Field label="Make"><Input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} /></Field>
          <Field label="Model"><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></Field>
          <Field label="Serial number"><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></Field>
          <Field label="Where to find it"><Input value={form.location_note} onChange={(e) => setForm({ ...form, location_note: e.target.value })} placeholder="Roof, north side" /></Field>
          <Field label="Installed"><Input type="date" value={form.installed_at} onChange={(e) => setForm({ ...form, installed_at: e.target.value })} /></Field>
          <Field label="Warranty expires"><Input type="date" value={form.warranty_expires_at} onChange={(e) => setForm({ ...form, warranty_expires_at: e.target.value })} /></Field>
        </div>
      </Modal>
    </Page>
  )
}

function AssetDetailModal({
  asset, clientName: client, onClose, onArchive, onOpenJob,
}: {
  asset: Asset; clientName: string; onClose: () => void; onArchive: () => void; onOpenJob: (id: number) => void
}) {
  const history = useAssetHistory(asset.id)
  const w = warrantyState(asset)

  return (
    <Modal
      open
      onClose={onClose}
      title={asset.name}
      wide
      footer={<>
        <button className={btnSecondary} onClick={onArchive}>Archive</button>
        <button className={btnPrimary} onClick={onClose}>Close</button>
      </>}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3 text-[13px] sm:grid-cols-3">
          {[
            ['Client', client],
            ['Address', asset.address || '—'],
            ['Where to find it', asset.location_note || '—'],
            ['Make / model', [asset.make, asset.model].filter(Boolean).join(' ') || '—'],
            ['Serial', asset.serial_number || '—'],
            ['Category', asset.category || '—'],
            ['Installed', fmtDate(asset.installed_at)],
            ['Warranty', asset.warranty_expires_at ? `${fmtDate(asset.warranty_expires_at)} · ${WARRANTY_LABEL[w]}` : '—'],
            ['Service every', asset.service_interval_months ? `${asset.service_interval_months} months` : '—'],
          ].map(([k, v]) => (
            <div key={k as string}>
              <p className="text-token-secondary">{k}</p>
              <p className="font-medium text-token-primary">{v}</p>
            </div>
          ))}
        </div>

        {asset.notes && (
          <p className="whitespace-pre-wrap rounded-[10px] border border-token-subtle bg-token-surface p-3 text-[13px] text-token-primary">{asset.notes}</p>
        )}

        <div>
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-token-secondary">
            <Wrench className="h-3.5 w-3.5" /> Service history
          </p>
          {history.isLoading ? (
            <div className="h-16 animate-pulse rounded-[10px] bg-canvas-subtle" />
          ) : (history.data ?? []).length === 0 ? (
            <p className="text-[13px] text-token-secondary">No jobs recorded against this equipment yet.</p>
          ) : (
            <ul className="divide-y divide-token-subtle">
              {(history.data ?? []).map((j) => (
                <li key={j.id}>
                  <button onClick={() => onOpenJob(j.id)} className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:bg-token-surface">
                    <span className="min-w-0">
                      <span className="block truncate text-[13.5px] font-medium text-token-primary">{j.name || 'Job'}</span>
                      <span className="block text-[12px] text-token-secondary">{fmtDate(j.planned_start_date)}</span>
                    </span>
                    <span className="flex-none text-[12px] text-token-secondary">{j.status ?? ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  )
}
